import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DocumentArrowUpIcon,
  ArchiveBoxIcon,
  XMarkIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { bulkUploadSCG } from '../../services/api';

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const totalSize = (files) => files.reduce((sum, f) => sum + (f.size || 0), 0);

export default function BulkSCGUploader({ onUploaded }) {
  const [staged, setStaged] = useState([]);
  const [llmConcurrency, setLlmConcurrency] = useState(3);
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  const onDrop = useCallback((accepted, rejected) => {
    setError(null);
    if (rejected && rejected.length > 0) {
      setError(`Rejected ${rejected.length} file(s) — only PDFs and a single .zip are accepted.`);
    }
    if (accepted.length === 0) return;

    const hasZip = accepted.some((f) => f.name.toLowerCase().endsWith('.zip'));
    if (hasZip) {
      const zip = accepted.find((f) => f.name.toLowerCase().endsWith('.zip'));
      setStaged([zip]);
      return;
    }
    setStaged((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const next = [...prev];
      for (const f of accepted) {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) {
          seen.add(key);
          next.push(f);
        }
      }
      return next;
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
    },
    multiple: true,
    maxSize: 200 * 1024 * 1024,
  });

  const removeFile = (idx) => setStaged((prev) => prev.filter((_, i) => i !== idx));
  const clearAll = () => setStaged([]);

  const isZipBatch = staged.length === 1 && staged[0].name.toLowerCase().endsWith('.zip');

  const uploadMutation = useMutation({
    mutationFn: () => bulkUploadSCG(staged, llmConcurrency),
    onSuccess: (data) => {
      setStaged([]);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['bulk-scg-jobs'] });
      if (onUploaded) onUploaded(data.batch_id);
    },
    onError: (err) => {
      setError(err?.response?.data?.detail || err.message || 'Upload failed');
    },
  });

  return (
    <div className="bg-[var(--vault-surface)] border border-[var(--vault-border)] rounded-lg p-6">
      <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-1">Bulk SCG Upload</h2>
      <p className="text-sm text-[var(--text-secondary)] mb-4">
        Drag in many PDFs at once, or a single .zip of PDFs. Processing runs in the background and survives page navigation.
      </p>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${
          isDragActive
            ? 'border-[var(--spotlight-gold)] bg-[var(--spotlight-glow)]'
            : isDragReject
            ? 'border-[var(--error)] bg-[rgba(160,64,64,0.1)]'
            : 'border-[var(--vault-border)] hover:border-[var(--spotlight-gold)] bg-[var(--vault-bg)]'
        }`}
      >
        <input {...getInputProps()} />
        {isZipBatch ? (
          <ArchiveBoxIcon className="w-12 h-12 mx-auto text-[var(--spotlight-gold)] mb-3" />
        ) : (
          <DocumentArrowUpIcon className="w-12 h-12 mx-auto text-[var(--text-muted)] mb-3" />
        )}
        {isDragActive ? (
          <p className="text-[var(--spotlight-gold)] font-medium">Drop to add to the batch...</p>
        ) : (
          <>
            <p className="text-[var(--text-primary)] font-medium mb-1">
              Drag &amp; drop PDFs here, or a single .zip — or click to browse
            </p>
            <p className="text-sm text-[var(--text-secondary)]">PDF or ZIP, up to 200 MB per file</p>
          </>
        )}
      </div>

      {error && (
        <div className="mt-4 flex items-start gap-2 p-3 rounded bg-[rgba(160,64,64,0.15)] border border-[var(--error)] text-sm text-[var(--text-primary)]">
          <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-[var(--error)]" />
          <span>{error}</span>
        </div>
      )}

      {staged.length > 0 && (
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-[var(--text-primary)]">
              {isZipBatch
                ? `1 zip archive · ${formatFileSize(totalSize(staged))}`
                : `${staged.length} PDFs · ${formatFileSize(totalSize(staged))}`}
            </div>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] underline"
            >
              Clear
            </button>
          </div>

          <div className="max-h-48 overflow-y-auto border border-[var(--vault-border)] rounded bg-[var(--vault-bg)]">
            <table className="min-w-full text-sm">
              <tbody>
                {staged.map((f, idx) => (
                  <tr
                    key={`${f.name}-${idx}`}
                    className="border-b last:border-b-0 border-[var(--vault-border)]"
                  >
                    <td className="px-3 py-1.5 text-[var(--text-primary)] truncate max-w-md" title={f.name}>
                      {f.name}
                    </td>
                    <td className="px-3 py-1.5 text-[var(--text-muted)] text-xs whitespace-nowrap">
                      {formatFileSize(f.size)}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="text-[var(--text-muted)] hover:text-[var(--error)]"
                        title="Remove"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center gap-4">
            <label className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
              LLM concurrency
              <input
                type="number"
                min={1}
                max={8}
                value={llmConcurrency}
                onChange={(e) => setLlmConcurrency(Math.max(1, Math.min(8, parseInt(e.target.value || '1', 10))))}
                className="w-16 px-2 py-1 bg-[var(--vault-bg)] border border-[var(--vault-border)] rounded text-center text-[var(--text-primary)] focus:outline-none focus:border-[var(--spotlight-gold)]"
              />
            </label>
            <button
              type="button"
              disabled={uploadMutation.isLoading || staged.length === 0}
              onClick={() => uploadMutation.mutate()}
              className="ml-auto px-4 py-2 bg-[var(--spotlight-gold)] text-[var(--vault-bg)] rounded font-medium hover:bg-[var(--spotlight-amber)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploadMutation.isLoading ? 'Uploading…' : `Start batch (${staged.length})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
